import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Relatorios.module.css';
import { listar, COLECOES } from '../../services/firestore';
import { exportarPDF, exportarExcel } from '../../utils/exportar';

// ============================================================
// RELATÓRIO: CONSUMO POR OS
// ============================================================
// Soma o consumo (saídas - devoluções) por OS, agrupando por
// produto. Útil para custeio e fechamento de obras.
// ============================================================

export default function RelConsumoOS() {
  const navigate = useNavigate();

  const [movimentacoes, setMovimentacoes] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtroOS, setFiltroOS] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [agrupamento, setAgrupamento] = useState('os');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [movs, oss] = await Promise.all([
        listar(COLECOES.MOVIMENTACOES),
        listar(COLECOES.ORDENS_SERVICO),
      ]);
      setMovimentacoes(movs);
      setOrdens(oss);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const linhas = useMemo(() => {
    const mapa = {};

    movimentacoes.forEach((m) => {
      if (m.status !== 'EFETIVADA') return;
      if (!m.os) return;
      if (m.tipo !== 'SAIDA' && m.tipo !== 'DEVOLUCAO') return;
      if (filtroOS && m.os !== filtroOS) return;

      const sinal = m.tipo === 'SAIDA' ? 1 : -1;
      const itens = Array.isArray(m.itens) ? m.itens : [];
      itens.forEach((it) => {
        const chave = `${m.os}::${it.produtoId}`;
        if (!mapa[chave]) {
          mapa[chave] = {
            os: m.os,
            produtoId: it.produtoId,
            produto: it.cor || it.produtoNome || it.nome || '—',
            cor: it.cor || '',
            qtdConsumida: 0,
            saidas: 0,
            devolucoes: 0,
          };
        }
        const qtd = Number(it.qtd) || 0;
        mapa[chave].qtdConsumida += sinal * qtd;
        if (sinal > 0) mapa[chave].saidas += qtd;
        else mapa[chave].devolucoes += qtd;
      });
    });

    let r = Object.values(mapa).filter((x) => x.qtdConsumida > 0);

    const mapaOS = {};
    ordens.forEach((o) => {
      mapaOS[o.numero] = o;
    });
    r = r.map((x) => ({
      ...x,
      osStatus: mapaOS[x.os]?.status || '—',
      osCliente: mapaOS[x.os]?.clienteNome || '',
    }));

    if (filtroStatus) {
      r = r.filter((x) => x.osStatus === filtroStatus);
    }

    return r.sort((a, b) => {
      const o = (b.os || '').localeCompare(a.os || '');
      if (o !== 0) return o;
      return a.produto.localeCompare(b.produto);
    });
  }, [movimentacoes, ordens, filtroOS, filtroStatus]);

  const linhasAgrupadasPorOS = useMemo(() => {
    if (agrupamento !== 'os') return null;
    const mapa = {};
    linhas.forEach((l) => {
      if (!mapa[l.os]) {
        mapa[l.os] = {
          os: l.os,
          cliente: l.osCliente,
          status: l.osStatus,
          itens: [],
          totalProdutos: 0,
        };
      }
      mapa[l.os].itens.push(l);
      mapa[l.os].totalProdutos = mapa[l.os].itens.length;
    });
    return Object.values(mapa);
  }, [linhas, agrupamento]);

  const opcoesOS = useMemo(
    () => [...new Set(linhas.map((l) => l.os))].sort().reverse(),
    [linhas]
  );

  const resumo = useMemo(() => {
    const oss = new Set(linhas.map((l) => l.os)).size;
    const produtos = new Set(linhas.map((l) => l.produtoId)).size;
    return { oss, produtos, linhasTotal: linhas.length };
  }, [linhas]);

  const colunasExport = [
    { header: 'OS', dataKey: 'os' },
    { header: 'Cliente', dataKey: 'osCliente' },
    { header: 'Status OS', dataKey: 'osStatus' },
    { header: 'Produto', dataKey: 'produto' },
    { header: 'Cor', dataKey: 'cor' },
    { header: 'Saídas', dataKey: 'saidas' },
    { header: 'Devoluções', dataKey: 'devolucoes' },
    { header: 'Consumo líq. (L)', dataKey: 'qtdConsumida' },
  ];

  function handlePDF() {
    const sub = filtroOS ? `OS: ${filtroOS}` : '';
    exportarPDF({
      titulo: 'Consumo por OS',
      subtitulo: sub,
      colunas: colunasExport,
      dados: linhas,
      orientacao: 'landscape',
    });
  }

  function handleExcel() {
    exportarExcel({
      titulo: 'Consumo por OS',
      colunas: colunasExport,
      dados: linhas,
      nomeAba: 'Consumo OS',
    });
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Consumo por OS</div>
          <div className={styles.pageSub}>
            Quanto de cada produto foi consumido em cada ordem de serviço.
          </div>
        </div>
        <button
          className={styles.btnVoltar}
          onClick={() => navigate('/relatorios')}
        >
          ← Voltar para Relatórios
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '14px 0',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.resumo} style={{ marginTop: 14 }}>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Ordens de serviço</div>
          <div className={styles.resumoValor}>{resumo.oss}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Produtos distintos</div>
          <div className={styles.resumoValor}>{resumo.produtos}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Linhas (OS × produto)</div>
          <div className={styles.resumoValor}>{resumo.linhasTotal}</div>
        </div>
      </div>

      <div className={styles.filtros}>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>OS</label>
          <select
            className={styles.filtroSelect}
            value={filtroOS}
            onChange={(e) => setFiltroOS(e.target.value)}
          >
            <option value="">Todas</option>
            {opcoesOS.map((os) => (
              <option key={os} value={os}>{os}</option>
            ))}
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Status da OS</label>
          <select
            className={styles.filtroSelect}
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="Aberta">Aberta</option>
            <option value="Em execução">Em execução</option>
            <option value="Concluída">Concluída</option>
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Visualizar</label>
          <select
            className={styles.filtroSelect}
            value={agrupamento}
            onChange={(e) => setAgrupamento(e.target.value)}
          >
            <option value="os">Agrupado por OS</option>
            <option value="produto">Lista linear</option>
          </select>
        </div>

        <div className={styles.btnExportar}>
          <button
            className={styles.btnExp}
            onClick={handlePDF}
            disabled={linhas.length === 0}
          >
            📄 PDF
          </button>
          <button
            className={styles.btnExp}
            onClick={handleExcel}
            disabled={linhas.length === 0}
          >
            📊 Excel
          </button>
        </div>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {linhas.length} linhas · {resumo.oss}{' '}
            {resumo.oss === 1 ? 'OS' : 'OSs'}
          </span>
        </div>
        <div className={styles.tabelaScroll}>
          <table className={styles.tabela}>
            <thead>
              <tr>
                {agrupamento === 'os' ? (
                  <>
                    <th>OS / Produto</th>
                    <th>Cliente</th>
                    <th>Status</th>
                    <th className={styles.textRight}>Saídas</th>
                    <th className={styles.textRight}>Devoluções</th>
                    <th className={styles.textRight}>Consumo líq.</th>
                  </>
                ) : (
                  <>
                    <th>OS</th>
                    <th>Cliente</th>
                    <th>Status</th>
                    <th>Produto</th>
                    <th className={styles.textRight}>Saídas</th>
                    <th className={styles.textRight}>Devoluções</th>
                    <th className={styles.textRight}>Consumo líq.</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Carregando...
                  </td>
                </tr>
              ) : linhas.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Nenhum consumo encontrado.
                  </td>
                </tr>
              ) : agrupamento === 'os' ? (
                linhasAgrupadasPorOS.map((grupo) => (
                  <>
                    <tr
                      key={`os-${grupo.os}`}
                      style={{ background: '#fafafa', fontWeight: 600 }}
                    >
                      <td className={styles.mono}>
                        <strong>{grupo.os}</strong>
                        <span className={styles.muted}>
                          {' · '}
                          {grupo.totalProdutos}{' '}
                          {grupo.totalProdutos === 1 ? 'produto' : 'produtos'}
                        </span>
                      </td>
                      <td>{grupo.cliente || '—'}</td>
                      <td>
                        <span className={`${styles.badge} ${styles.badgeNeutro}`}>
                          {grupo.status}
                        </span>
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                    {grupo.itens.map((l, idx) => (
                      <tr key={`os-${grupo.os}-item-${idx}`}>
                        <td style={{ paddingLeft: 32 }}>
                          {l.produto}
                          {l.cor && (
                            <div className={styles.muted} style={{ fontSize: 10 }}>
                              {l.cor}
                            </div>
                          )}
                        </td>
                        <td></td>
                        <td></td>
                        <td className={styles.textRight}>{l.saidas}</td>
                        <td className={styles.textRight}>{l.devolucoes}</td>
                        <td className={styles.textRight}>
                          <strong>{l.qtdConsumida} L</strong>
                        </td>
                      </tr>
                    ))}
                  </>
                ))
              ) : (
                linhas.map((l, idx) => (
                  <tr key={idx}>
                    <td className={styles.mono}><strong>{l.os}</strong></td>
                    <td>{l.osCliente || '—'}</td>
                    <td>
                      <span className={`${styles.badge} ${styles.badgeNeutro}`}>
                        {l.osStatus}
                      </span>
                    </td>
                    <td>
                      <strong>{l.produto}</strong>
                      {l.cor && (
                        <div className={styles.muted} style={{ fontSize: 10 }}>
                          {l.cor}
                        </div>
                      )}
                    </td>
                    <td className={styles.textRight}>{l.saidas}</td>
                    <td className={styles.textRight}>{l.devolucoes}</td>
                    <td className={styles.textRight}>
                      <strong>{l.qtdConsumida} L</strong>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
